import { lazy, Suspense } from 'react';
import { MantineProvider, LoadingOverlay, Center } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import { theme } from './theme';
import { AuthProvider } from './providers/AuthProvider';
import { CompanyProvider } from './providers/CompanyProvider';
import { AppLayout } from './components/layout/AppLayout';
import { useAuth } from './hooks/useAuth';

// Lazy-loaded pages for code splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const InviteAcceptPage = lazy(() => import('./pages/auth/InviteAcceptPage').then(m => ({ default: m.InviteAcceptPage })));
const ServicesPage = lazy(() => import('./pages/services/ServicesPage').then(m => ({ default: m.ServicesPage })));
const BillingPage = lazy(() => import('./pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const PaymentReturnPage = lazy(() => import('./pages/billing/PaymentReturnPage').then(m => ({ default: m.PaymentReturnPage })));
const EmployeesPage = lazy(() => import('./pages/employees/EmployeesPage').then(m => ({ default: m.EmployeesPage })));
const DomainsPage = lazy(() => import('./pages/domains/DomainsPage').then(m => ({ default: m.DomainsPage })));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));

/**
 * Protected route wrapper
 * Redirects to login if not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Center h="100vh">
        <LoadingOverlay visible />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Public route wrapper
 * Redirects to dashboard if already authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Center h="100vh">
        <LoadingOverlay visible />
      </Center>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Page loading fallback
 */
function PageLoader() {
  return (
    <Center h="100vh">
      <LoadingOverlay visible />
    </Center>
  );
}

/**
 * App routes wrapped in auth context
 */
function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Semi-public route: handles auth check internally */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <CompanyProvider>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/services" replace />} />
                      <Route path="/services" element={<ServicesPage />} />
                      <Route path="/billing" element={<BillingPage />} />
                      <Route path="/billing/return" element={<PaymentReturnPage />} />
                      <Route path="/employees" element={<EmployeesPage />} />
                      <Route path="/domains" element={<DomainsPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                    </Routes>
                  </Suspense>
                </AppLayout>
              </CompanyProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </MantineProvider>
  );
}

export default App;
